import { useMemo } from "react";
import { BackendApi } from "../../../api/api.ts";
import { QuinnBusinessOption } from "../types";
import { isServerChatId } from "../utils/storage.ts";
import type {QuinnChatScopeEnum, QuinnRatingEnum, QuinnRunStatusEnum, QuinnRunStepTypeEnum} from "../../../generated/enums.ts";
import type { BackendAPITypes } from "../../../generated/blueprint.ts";

export type QuinnStartRunResult = {
    run_id: string;
    chat_id: string;
    status: QuinnRunStatusEnum;
};

export type QuinnRunStatusPrompt = {
    kind: string;
    question: string;
    help_text?: string | null;
    options: Array<{ value: string; label: string; hint?: string | null }>;
    multiple?: boolean;
    allow_free_text?: boolean;
};

export type QuinnRunStatusDocumentSource =
    BackendAPITypes.BACKENDPreHandleSyncApiV1QuinnGetRunStatusGetResponseBody["sources"][number];

export type QuinnRunStatusResult = {
    id: string;
    chat_id: string;
    status: QuinnRunStatusEnum;
    steps: Array<{
        id: string;
        step_index: number;
        step_type: QuinnRunStepTypeEnum;
        label: string;
        created_at: string;
    }>;
    answer?: string | null;
    artifacts?: object[];
    needs_clarification?: boolean;
    prompt?: QuinnRunStatusPrompt | null;
    sources?: QuinnRunStatusDocumentSource[];
};

export type QuinnSessionResult = {
    id: string;
    title?: string | null;
    client_business_id?: string | null;
    client_scope?: string | null;
    created_at: string;
    last_message_at: string;
};

export type QuinnApi = {
    startRun: (params: {
        content: string;
        chatId: string | null;
        entrepreneurBusinessId: string | null;
        clientScope: QuinnChatScopeEnum;
        replyToArtifactId: string | null;
    }) => Promise<QuinnStartRunResult>;
    getRunStatus: (runId: string) => Promise<QuinnRunStatusResult>;
    rateAnswer: (runId: string, rating: QuinnRatingEnum | null) => Promise<void>;
    getChat: (chatId?: string | null) => Promise<unknown>;
    listSessions: () => Promise<{ sessions?: QuinnSessionResult[] }>;
    listBusinesses: () => Promise<QuinnBusinessOption[]>;
};

export function createQuinnApi(backend: BackendApi = new BackendApi()): QuinnApi {
    return {
        startRun: ({ content, chatId, entrepreneurBusinessId, clientScope, replyToArtifactId }) =>
            backend.Quinn__startRun({
                content,
                chat_id: isServerChatId(chatId) ? chatId : null,
                entrepreneur_business_id: entrepreneurBusinessId ?? null,
                client_scope: clientScope,
                reply_to_artifact_id: replyToArtifactId,
            }),
        getRunStatus: (runId) => backend.Quinn__getRunStatus(runId),
        rateAnswer: async (runId, rating) => {
            await backend.Quinn__rateAnswer({ run_id: runId, rating });
        },
        getChat: (chatId) => backend.Quinn__getChat(isServerChatId(chatId) ? chatId : null),
        listSessions: () => backend.Quinn__listSessions({}),
        listBusinesses: async () => {
            const response = await backend.Partner__myClients__list({});
            const businesses = response?.businesses ?? [];
            return businesses.map((business) => ({
                id: String(business.id),
                name: business.name ?? "Onbekende klant",
            }));
        },
    };
}

export function useQuinnApi(): QuinnApi {
    return useMemo(() => createQuinnApi(), []);
}
